import React, { useContext, useEffect, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";

import { styled, useTheme } from "@mui/material/styles";
import { tokens } from "../../../../theme";
import ReactApexChart from 'react-apexcharts';
import useApi from "../../../shared/hooks/useApi";
import { chartOptions } from "./chartOptions";
import PredictionChart from "./predictionChart";

import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import LoadingScreen from "../../../shared/components/LoadingScreen/LoadingScreen";
import SetpointPredictionChart from "./setpointPredictionChart";
import MarketDataChart from "./marketDataChart";
const Custom = ({ isPlanningOpen, setIsPlanningOpen, isSettingsOpen, setIsSettingsOpen }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { apiPost, apiGet, loadingApi, error } = useApi();
    const [columns, setColumns] = useState([]);
    const [chartData, setChartData] = useState({
        series: [],
        options: chartOptions.options
    });

    const [openOptionList, setOpenOptionList] = useState(false);
    const [from, setFrom] = useState(dayjs().set("hour", 0));
    const [to, setTo] = useState(dayjs().set("hour", 24));
    const [applyFrom, setApplyFrom] = useState(dayjs().set("hour", 0));
    const [applyTo, setApplyTo] = useState(dayjs().set("hour", 24));
    const [selectedOptions, setSelectedOptions] = useState(['BELERE_UTI.W95-TAN1-FILL_FLEX.SP',
        'BELERE_UTI.W95-BO1-CV01_FLEX.SP',
        'BELERE_UTI.W55-TAN1-FILL_FLEX.SP',
    ]);
    const [loadingGetColumns, setLoadingGetColumns] = useState(false);
    const [loadingGetData, setLoadingGetData] = useState(false);
    const [loadingPredictionData, setLoadingPredictionData] = useState(false);

    useEffect(() => {
        setLoadingGetColumns(true);
        try {
            const getColumnsRoute = "/esid-24-003/get-columns";
            const getColumns = async () => {
                const columns = await apiGet("UX_HTTP_API", getColumnsRoute);
                const fullCols = [...columns.realtime_setpoints, ...columns.realtime_values];
                setColumns(fullCols);
                setLoadingGetColumns(false);
            };
            const fromDate = from.toISOString();
            const toDate = to.toISOString();
            const getSpotPricesRoute = "/common/market-data?from=" + fromDate + "&to=" + toDate + "&data=electricity_day_ahead_prices&version=1";
            
           /*  const getSpotPrices = async () => {
                const spotPrices = await apiGet("UX_HTTP_API", getSpotPricesRoute);
                console.log("****",spotPrices);
            };
            getSpotPrices(); */
            getColumns()
        } catch (error) {
            console.error("Error getting columns:", error);
            setLoadingGetColumns(false);
        }
    }, []);

    useEffect(() => {
        const fromDate = from.toISOString();  // fromDate is your Dayjs object
        const toDate = to.toISOString();      // toDate is your Dayjs object
        const getDataRoute = `/esid-24-003/get-data?from=${fromDate}&to=${toDate}&columns=${encodeURIComponent(selectedOptions.join(","))}`;
        try {
            const getData = async () => {
                setLoadingGetData(true);
                const data = await apiGet("UX_HTTP_API", getDataRoute);
                if (data) {
                    const series = Object.entries(data).map(([key, dataPoints]) => ({
                        name: key,
                        data: dataPoints.map(point => ({
                            x: new Date(point.datetime).getTime(),
                            y: Math.floor(point.value)
                        }))
                    }));

                    setChartData(prev => ({
                        ...prev,
                        series
                    }));
                    setLoadingGetData(false);
                }
            };
            getData();
        } catch (error) {
            console.error("Error getting data:", error);
            setLoadingGetData(false);
        } finally {
            setLoadingGetData(false);
        }
    }, []);

    const applyFilters = () => {
        try {
            const fromDate = from.toISOString();  // fromDate is your Dayjs object
            const toDate = to.toISOString();      // toDate is your Dayjs object
            const getDataRoute = `/esid-24-003/get-data?from=${fromDate}&to=${toDate}&columns=${encodeURIComponent(selectedOptions.join(","))}`;
            // Add a console.log to see the final URL
            setApplyFrom(from);
            setApplyTo(to);
            try {
                const getData = async () => {
                    setLoadingGetData(true);
                    const data = await apiGet("UX_HTTP_API", getDataRoute);
                    if (data) {
                        const series = Object.entries(data).map(([key, dataPoints]) => ({
                            name: key,
                            data: dataPoints.map(point => ({
                                x: new Date(point.datetime).getTime(),
                                y: Math.floor(point.value)
                            }))
                        }));

                        setChartData(prev => ({
                            ...prev,
                            series
                        }));
                        setLoadingGetData(false);
                    }
                };
                getData();
            } catch (error) {
                console.error("Error applying filters:", error);
                setLoadingGetData(false);
            } finally {
                setLoadingGetData(false);
            }
        } catch (error) {
            console.error("Error applying filters:", error);
            setLoadingGetData(false);
        }
    }

    const removeSelectedOption = (option) => {
        setSelectedOptions(selectedOptions.filter(item => item !== option));
    }

    return (
        <Box sx={styles(colors).container}>
            {(loadingGetColumns || loadingGetData || loadingPredictionData) && (
                <LoadingScreen loading={loadingGetColumns || loadingGetData || loadingPredictionData} />
            )}
            <Box sx={styles(colors).graphBox}>
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start", mt: 2, mb: 4 }}>
                    <Box >
                        <LocalizationProvider dateAdapter={AdapterDayjs} sx={{ margin: 2 }}>
                            <DemoContainer components={['DatePicker']}>
                                <DatePicker format="DD/MM/YYYY" label="From" value={from} onChange={(date) => setFrom(date)} />
                            </DemoContainer>
                        </LocalizationProvider>
                    </Box>
                    <Box sx={{ marginLeft: 10 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} sx={{ margin: 2 }}>
                            <DemoContainer components={['DatePicker']}>
                                <DatePicker format="DD/MM/YYYY"  label="To" value={to} onChange={(date) => setTo(date)} />
                            </DemoContainer>
                        </LocalizationProvider>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start", mt: 2, mb: 4 }}>
                    {selectedOptions.map((option) => (
                        <Button key={option} sx={selectedOptions.includes(option) ? styles(colors).selectedColumnButton :
                            styles(colors).columnButton} onClick={() => removeSelectedOption(option)}>{option}</Button>
                    ))}
                    <Button onClick={() => setOpenOptionList(!openOptionList)}><MoreHorizIcon fontSize="medium" /></Button>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start", mt: 2, mb: 4 }}>
                    <Button sx={{ marginLeft: 2, color: "#fff", bgcolor: "#635781", "&:hover": { bgcolor: "#63578180" } }} onClick={applyFilters}>Apply Filters</Button>
                </Box>

                <Dialog open={openOptionList} onClose={() => setOpenOptionList(false)}>
                    <DialogTitle>Select Columns</DialogTitle>
                    <DialogContent>
                        {columns.map((column) => (
                            <Button key={column} sx={selectedOptions.includes(column) ? styles(colors).selectedColumnButton :
                                styles(colors).columnButton} onClick={() => selectedOptions.includes(column) ? removeSelectedOption(column) : setSelectedOptions([...selectedOptions, column])}>{column}</Button>
                        ))}
                    </DialogContent>
                </Dialog>
                <ReactApexChart
                    options={chartData.options}
                    series={chartData.series}
                    type="line"
                    height={450}

                />

            </Box>

            <Box sx={styles(colors).graphBox}>
                <PredictionChart setLoading={setLoadingPredictionData} from={applyFrom} to={applyTo} />
            </Box>
            <Box sx={styles(colors).graphBox}>
                <SetpointPredictionChart setLoading={setLoadingPredictionData} from={applyFrom} to={applyTo} />
            </Box>
           {/*  <Box sx={styles(colors).graphBox}>
                <MarketDataChart setLoading={setLoadingPredictionData} from={applyFrom} to={applyTo}  />
            </Box> */}
        </Box >
    );
};

export default Custom

const styles = (colors) => ({
    container: {
        justifyContent: "center",
        width: "95%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginLeft: "2.5%",
        paddingLeft: "50px",
    },
    columnButton: {
        width: "160px",
        backgroundColor: colors.greenAccent[100],
        borderRadius: "10px",
        margin: "10px",
        flexWrap: "wrap",
        color: "white",
        padding: "10px",
        fontSize: "10px",
        "&:hover": {
            backgroundColor: colors.greenAccent[200],
        }
    },
    selectedColumnButton: {
        width: "160px",
        backgroundColor: colors.entras_green[100],
        borderRadius: "10px",
        color: "white",
        margin: "10px",
        flexWrap: "wrap",
        padding: "5px",
        fontSize: "11px",
        "&:hover": {
            backgroundColor: colors.entras_green[200],
        }
    },
    graphBox: {
        width: "100%",
        height: "100%",
        backgroundColor: "#fff",
        borderRadius: 3,
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        p: 2,
        pb: 4,
        alignContent: "center",
        marginTop: "20px",
    }
})

